import React, { useCallback, useEffect, useState } from 'react';
import {
  Loader2, RefreshCw, Plus, Save, FileText, ListTree, AlertTriangle,
  CheckCircle, Globe, Eye, EyeOff, Trash2, ChevronDown, ChevronUp,
} from 'lucide-react';
import {
  type DnsRecordRow,
  type DnsRowType,
  parseZoneFile,
  serializeZone,
  cloneParsedForEdit,
} from '../bind/zoneParse';
import { API, apiFetch } from '../apiClient';

type ServerRow = { id: string; label: string; host: string; port: number; username: string };
type ServerStatus = 'unknown' | 'online' | 'offline' | 'testing';

interface BindDnsPageProps {
  servers: ServerRow[];
  passwords: Record<string, string>;
  setPassword: (id: string, v: string) => void;
  showPw: Record<string, boolean>;
  setShowPw: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  serverStatus: Record<string, { status: ServerStatus; info: string }>;
  testConnection: (server: ServerRow) => void;
}

export default function BindDnsPage({
  servers,
  passwords,
  setPassword,
  showPw,
  setShowPw,
  serverStatus,
  testConnection,
}: BindDnsPageProps) {
  const [serverId, setServerId] = useState(servers[0]?.id || '');
  const [zones, setZones] = useState<string[]>([]);
  const [selectedZone, setSelectedZone] = useState<string>('');
  const [rawContent, setRawContent] = useState('');
  const [parsed, setParsed] = useState(() => cloneParsedForEdit(parseZoneFile('')));
  const [tab, setTab] = useState<'records' | 'raw'>('records');
  const [namedLocal, setNamedLocal] = useState('');
  const [namedOpen, setNamedOpen] = useState(false);
  const [loadingZones, setLoadingZones] = useState(false);
  const [loadingZone, setLoadingZone] = useState(false);
  const [savingZone, setSavingZone] = useState(false);
  const [loadingNamed, setLoadingNamed] = useState(false);
  const [savingNamed, setSavingNamed] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [addApex, setAddApex] = useState('');
  const [addNs, setAddNs] = useState('ns1.mapnexa.com\nns2.mapnexa.com');
  const [creating, setCreating] = useState(false);

  const pwPayload = () => Object.fromEntries(Object.entries(passwords).filter(([, v]) => v.trim()));

  const post = useCallback(async (path: string, body: object) => {
    const r = await apiFetch(`${API}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ passwords: pwPayload(), ...body }),
    });
    return r.json();
  }, [passwords]);

  const fetchZones = useCallback(async () => {
    if (!serverId || !passwords[serverId]?.trim()) {
      setZones([]);
      return;
    }
    setLoadingZones(true);
    setErr(null);
    try {
      const r = await post(`/api/bind/${serverId}/zones/list`, {});
      if (r.error) {
        setErr(r.error);
        setZones([]);
        return;
      }
      setZones(r.zones || []);
    } catch (e: any) {
      setErr(e?.message || String(e));
      setZones([]);
    } finally {
      setLoadingZones(false);
    }
  }, [serverId, passwords, post]);

  useEffect(() => {
    fetchZones();
  }, [fetchZones]);

  const loadZoneFile = async (fileName: string) => {
    if (!serverId || !fileName) return;
    setLoadingZone(true);
    setErr(null);
    setMsg(null);
    try {
      const r = await post(`/api/bind/${serverId}/zone/read`, { fileName });
      if (r.error) {
        setErr(r.error);
        setRawContent('');
        setParsed(cloneParsedForEdit(parseZoneFile('')));
        return;
      }
      const text = r.content || '';
      setRawContent(text);
      setParsed(cloneParsedForEdit(parseZoneFile(text)));
      setSelectedZone(fileName);
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setLoadingZone(false);
    }
  };

  const loadNamedLocal = async () => {
    if (!serverId || !passwords[serverId]?.trim()) return;
    setLoadingNamed(true);
    setErr(null);
    try {
      const r = await post(`/api/bind/${serverId}/named-local/read`, {});
      setNamedLocal(r.content || '');
      if (r.warning) setMsg(r.warning);
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setLoadingNamed(false);
    }
  };

  useEffect(() => {
    if (namedOpen && serverId) loadNamedLocal();
  }, [namedOpen, serverId]); // eslint-disable-line

  const saveZone = async (content: string) => {
    if (!serverId || !selectedZone) return;
    setSavingZone(true);
    setErr(null);
    setMsg(null);
    try {
      const r = await post(`/api/bind/${serverId}/zone/save`, { fileName: selectedZone, content });
      if (r.error) {
        setErr(r.error);
        return;
      }
      if (!r.success) {
        setErr(r.checkOutput || 'named-checkzone failed');
        return;
      }
      setMsg([r.checkOutput, r.reloadOutput].filter(Boolean).join('\n') || 'Saved and BIND reloaded.');
      setRawContent(content);
      setParsed(cloneParsedForEdit(parseZoneFile(content)));
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setSavingZone(false);
    }
  };

  const saveFromRecords = () => {
    const text = serializeZone(parsed);
    saveZone(text);
  };

  const saveFromRaw = () => saveZone(rawContent);

  const saveNamed = async () => {
    if (!serverId) return;
    setSavingNamed(true);
    setErr(null);
    setMsg(null);
    try {
      const r = await post(`/api/bind/${serverId}/named-local/save`, { content: namedLocal });
      if (r.error) {
        setErr(r.error);
        return;
      }
      if (!r.success) {
        setErr(r.checkOutput || 'named-checkconf failed');
        return;
      }
      setMsg([r.checkOutput, r.reloadOutput].filter(Boolean).join('\n') || 'named.conf.local saved.');
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setSavingNamed(false);
    }
  };

  const createZone = async () => {
    const apex = addApex.trim().toLowerCase();
    if (!serverId || !apex) return;
    const nsHosts = addNs.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
    setCreating(true);
    setErr(null);
    setMsg(null);
    try {
      const r = await post(`/api/bind/${serverId}/zone/create`, { apexDomain: apex, nsHosts });
      if (r.error) {
        setErr(r.error);
        return;
      }
      if (!r.success) {
        setErr(r.output || 'Create failed');
        return;
      }
      setMsg(r.reloadOutput || r.checkzone || 'Zone created.');
      setAddOpen(false);
      setAddApex('');
      await fetchZones();
      if (r.fileName) await loadZoneFile(r.fileName);
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setCreating(false);
    }
  };

  const addRecordRow = () => {
    setParsed((p) => ({
      ...p,
      records: [
        ...p.records,
        { id: `r${Date.now()}`, host: '@', ttl: '3600', type: 'A' as DnsRowType, value: '' },
      ],
    }));
  };

  const patchRecord = (id: string, patch: Partial<DnsRecordRow>) => {
    setParsed((p) => ({
      ...p,
      records: p.records.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    }));
  };

  const removeRecord = (id: string) => {
    setParsed((p) => ({ ...p, records: p.records.filter((r) => r.id !== id) }));
  };

  const onTabRaw = () => {
    setRawContent(serializeZone(parsed));
    setTab('raw');
  };

  const onTabRecords = () => {
    setParsed(cloneParsedForEdit(parseZoneFile(rawContent)));
    setTab('records');
  };

  const sv = servers.find((s) => s.id === serverId);
  const st = serverId ? serverStatus[serverId] : undefined;

  return (
    <div className="hub-outlet dns-hub-outlet">
      <div className="dns-shell">
        <div className="hub-page-head">
          <h1 className="hub-page-h1">DNS / BIND</h1>
          <p className="hub-page-sub">
            Manage zone files under <code>/etc/bind/zones</code> and <code>named.conf.local</code>
          </p>
        </div>
        <div className="dns-toolbar">
          <div className="dns-toolbar-left">
            <label className="dns-field">
              <span>Server</span>
              <select className="commit-select dns-server-select" value={serverId} onChange={(e) => { setServerId(e.target.value); setSelectedZone(''); setRawContent(''); setParsed(cloneParsedForEdit(parseZoneFile(''))); }}>
                {servers.length === 0 && <option value="">No servers</option>}
                {servers.map((s) => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </label>
            {sv && (
              <label className="dns-field dns-pw-field">
                <span>SSH password</span>
                <div className="dns-pw-wrap">
                  <input
                    type={showPw[serverId] ? 'text' : 'password'}
                    className="cfg-input"
                    placeholder="Password"
                    value={passwords[serverId] || ''}
                    onChange={(e) => setPassword(serverId, e.target.value)}
                    autoComplete="off"
                  />
                  <button type="button" className="btn btn-ghost btn-xs" onClick={() => setShowPw((p) => ({ ...p, [serverId]: !p[serverId] }))}>
                    {showPw[serverId] ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </label>
            )}
          </div>
          <div className="dns-toolbar-right">
            <button type="button" className="btn btn-ghost btn-sm" disabled={!serverId || !passwords[serverId]?.trim()} onClick={() => sv && testConnection(sv)}>
              Test SSH
            </button>
            {st?.status === 'online' && <span className="dns-status ok">Online</span>}
            {st?.status === 'offline' && <span className="dns-status bad">Offline</span>}
            {st?.status === 'testing' && <span className="dns-status"><Loader2 size={12} className="spin" /></span>}
            <button type="button" className="btn btn-ghost btn-sm" disabled={loadingZones || !serverId} onClick={() => fetchZones()}>
              <RefreshCw size={12} className={loadingZones ? 'spin' : ''} /> Refresh zones
            </button>
            <button type="button" className="btn btn-deploy btn-sm" disabled={!serverId || !passwords[serverId]?.trim()} onClick={() => setAddOpen(true)}>
              <Plus size={12} /> Add zone
            </button>
          </div>
        </div>

        {err && (
          <div className="nginx-msg nginx-msg-err dns-banner">
            <AlertTriangle size={12} />{err}
          </div>
        )}
        {msg && !err && (
          <div className="nginx-msg nginx-msg-ok dns-banner">
            <CheckCircle size={12} />
            <pre className="dns-msg-pre">{msg}</pre>
          </div>
        )}

        <div className="dns-body">
          <aside className="dns-sidebar">
            <div className="dns-sidebar-head">Zone files</div>
            <div className="dns-sidebar-scroll">
              {loadingZones ? (
                <div className="dns-loading"><Loader2 className="spin" size={18} /></div>
              ) : zones.length === 0 ? (
                <p className="dns-empty">No zone files found (or enter password).</p>
              ) : (
                <ul className="dns-zone-list">
                  {zones.map((z) => (
                    <li key={z}>
                      <button type="button" className={selectedZone === z ? 'active' : ''} onClick={() => loadZoneFile(z)}>
                        {z}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="dns-sidebar-foot">
              <button
                type="button"
                className={`dns-named-sidebar-btn ${namedOpen ? 'open' : ''}`}
                onClick={() => setNamedOpen((o) => !o)}
              >
                <FileText size={14} />
                <span>named.conf.local</span>
                {namedOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {namedOpen && (
                <div className="dns-named-inline">
                  <p className="dns-hint warn">Syntax errors can stop BIND. <code>named-checkconf</code> must pass.</p>
                  <div className="dns-named-actions">
                    <button type="button" className="btn btn-ghost btn-xs" disabled={loadingNamed} onClick={() => loadNamedLocal()}>
                      {loadingNamed ? <Loader2 size={11} className="spin" /> : <RefreshCw size={11} />}
                      Reload
                    </button>
                    <button type="button" className="btn btn-deploy btn-xs" disabled={savingNamed || !serverId} onClick={saveNamed}>
                      {savingNamed ? <Loader2 size={11} className="spin" /> : <Save size={11} />}
                      Save
                    </button>
                  </div>
                  <textarea className="nginx-textarea dns-named-ta-sidebar" value={namedLocal} onChange={(e) => setNamedLocal(e.target.value)} spellCheck={false} />
                </div>
              )}
            </div>
          </aside>

          <main className="dns-main">
            {!selectedZone ? (
              <div className="dns-placeholder-wrap">
                <FileText size={28} className="dns-placeholder-icon" aria-hidden />
                <p className="dns-placeholder">Select a zone file from the list.</p>
              </div>
            ) : loadingZone ? (
              <div className="dns-loading"><Loader2 className="spin" size={22} /></div>
            ) : (
              <div className="dns-main-inner">
                <div className="dns-tabs dns-tabs-seg">
                  <button type="button" className={tab === 'records' ? 'active' : ''} onClick={onTabRecords}>
                    <ListTree size={14} /> Records
                  </button>
                  <button type="button" className={tab === 'raw' ? 'active' : ''} onClick={onTabRaw}>
                    <FileText size={14} /> Zone file (raw)
                  </button>
                </div>

                {tab === 'records' && (
                  <div className="dns-records">
                    <p className="dns-hint">SOA / NS stay in the preamble (open Raw to edit). Only A, AAAA, CNAME, MX, and TXT rows are shown here; other lines remain in Raw.</p>
                    <div className="dns-table-wrap">
                      <table className="dns-table">
                        <thead>
                          <tr>
                            <th className="dns-col-host">Host</th>
                            <th className="dns-col-ttl">TTL</th>
                            <th className="dns-col-type">Type</th>
                            <th className="dns-col-val">Value</th>
                            <th className="dns-col-act" aria-label="Actions" />
                          </tr>
                        </thead>
                        <tbody>
                          {parsed.records.map((r) => (
                            <tr key={r.id}>
                              <td><input className="cfg-input dns-cell" value={r.host} onChange={(e) => patchRecord(r.id, { host: e.target.value })} /></td>
                              <td><input className="cfg-input dns-cell dns-ttl" value={r.ttl} onChange={(e) => patchRecord(r.id, { ttl: e.target.value })} /></td>
                              <td>
                                <select className="commit-select dns-type" value={r.type} onChange={(e) => patchRecord(r.id, { type: e.target.value as DnsRowType })}>
                                  {(['A', 'AAAA', 'CNAME', 'MX', 'TXT'] as const).map((t) => (
                                    <option key={t} value={t}>{t}</option>
                                  ))}
                                </select>
                              </td>
                              <td>
                                {r.type === 'MX' ? (
                                  <div className="dns-mx-wrap">
                                    <input className="cfg-input dns-cell dns-prio" placeholder="10" value={r.mxPriority || ''} onChange={(e) => patchRecord(r.id, { mxPriority: e.target.value })} />
                                    <input className="cfg-input dns-cell dns-mx-target" placeholder="mail.example.com." value={r.value} onChange={(e) => patchRecord(r.id, { value: e.target.value })} />
                                  </div>
                                ) : (
                                  <input className="cfg-input dns-cell dns-value" value={r.value} onChange={(e) => patchRecord(r.id, { value: e.target.value })} />
                                )}
                              </td>
                              <td className="dns-col-act">
                                <button type="button" className="btn btn-ghost btn-xs dns-del" onClick={() => removeRecord(r.id)} title="Remove row">
                                  <Trash2 size={14} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="dns-rec-actions">
                      <button type="button" className="btn btn-ghost btn-sm" onClick={addRecordRow}><Plus size={12} /> Add record</button>
                      <button type="button" className="btn btn-deploy btn-sm" disabled={savingZone} onClick={saveFromRecords}>
                        {savingZone ? <Loader2 size={12} className="spin" /> : <Save size={12} />}
                        Save zone
                      </button>
                    </div>
                  </div>
                )}

                {tab === 'raw' && (
                  <div className="dns-raw">
                    <textarea className="nginx-textarea dns-raw-ta" value={rawContent} onChange={(e) => setRawContent(e.target.value)} spellCheck={false} />
                    <button type="button" className="btn btn-deploy btn-sm" disabled={savingZone} onClick={saveFromRaw}>
                      {savingZone ? <Loader2 size={12} className="spin" /> : <Save size={12} />}
                      Save zone
                    </button>
                  </div>
                )}
              </div>
            )}
          </main>
        </div>
      </div>

      {addOpen && (
        <div className="nginx-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setAddOpen(false); }}>
          <div className="dns-modal" onMouseDown={(e) => e.stopPropagation()}>
            <h3>Add zone</h3>
            <p className="dns-hint">Creates <code>db.&lt;domain&gt;</code> and appends a <code>zone</code> block to <code>named.conf.local</code>.</p>
            <label className="dns-field vb-full">
              <span>Apex domain (e.g. app.mapnexa.com)</span>
              <input className="cfg-input" value={addApex} onChange={(e) => setAddApex(e.target.value)} placeholder="sub.example.com" />
            </label>
            <label className="dns-field vb-full">
              <span>Nameservers (one per line, optional)</span>
              <textarea className="cfg-input dns-ns-ta" value={addNs} onChange={(e) => setAddNs(e.target.value)} rows={3} />
            </label>
            <div className="dns-modal-btns">
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAddOpen(false)}>Cancel</button>
              <button type="button" className="btn btn-deploy btn-sm" disabled={creating || !addApex.trim()} onClick={createZone}>
                {creating ? <Loader2 size={12} className="spin" /> : <Plus size={12} />}
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
